import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  fechamentoLegivel,
  fechaNoDiaSeguinte,
  horarioLegivel,
  janelaCanonica,
  MEIA_NOITE_DO_FIM_DO_DIA,
} from '@arena/shared/schemas';
import { minutosDe } from '@arena/shared/tempo';
import type {
  ChaveConfiguracao,
  DuracaoSessaoHoras,
  FaixaHorarioNobre,
  JanelaHoraria,
} from '@arena/shared/schemas';
import type { PerfilNaMatriz } from '@arena/shared/schemas';
import { perfisApi } from '../api/acessos.js';
import { configuracoesApi, type ItemDoCatalogo } from '../api/configuracoes.js';
import { ErroDeApi, mensagemDoErro } from '../api/cliente.js';
import { useSessao } from '../auth/SessaoContext.jsx';
import { Erro, EsqueletoDeCard } from '../ui/estados.jsx';
import { Botao, Card, Nota } from '../ui/primitivos.jsx';
import { useToast } from '../ui/Toast.jsx';

/**
 * A tela de configurações da arena — pendência do documento 02, que o
 * protótipo não desenhou. Segue o padrão de `#m-plano`: um card por
 * assunto, campos em grade, e aqui um por CHAVE do catálogo de
 * `@arena/shared/schemas` — a mesma fonte que o `GET /configuracoes`
 * devolve, então uma chave nova no catálogo aparece na tela sem tocar
 * neste arquivo além de um `case`.
 *
 * Cada card gerencia o próprio rascunho e só grava quando alguém clica
 * Salvar — diferente da matriz de #v-acessos, que grava a cada clique.
 * A diferença é deliberada: célula é uma escolha de uma lista fechada
 * (sem "estado inválido no meio"); um horário ou um percentual, a
 * pessoa digita em etapas, e gravar a cada tecla salvaria rascunho.
 *
 * Ver: Arena_backend/Docs/plano/02-identidade-sessao.md
 */
export function Configuracoes() {
  const { pode } = useSessao();
  const podeEditar = pode('perfis', 'editar');

  const config = useQuery({ queryKey: ['configuracoes'], queryFn: configuracoesApi.listar });
  const perfis = useQuery({
    queryKey: ['acessos', 'matriz'],
    queryFn: perfisApi.matriz,
    enabled: podeEditar,
  });

  if (config.isPending) {
    return (
      <>
        <Cabecalho />
        <div className="grid g2">
          {[0, 1, 2, 3].map((i) => (
            <EsqueletoDeCard key={i} linhas={3} />
          ))}
        </div>
      </>
    );
  }

  if (config.isError) {
    return (
      <>
        <Cabecalho />
        <Erro detalhe={mensagemDoErro(config.error)} onTentarDeNovo={() => void config.refetch()} />
      </>
    );
  }

  const { valores, catalogo } = config.data;
  const item = (chave: string): ItemDoCatalogo | undefined =>
    catalogo.find((c) => c.chave === chave);

  return (
    <>
      <Cabecalho />
      <Nota>
        <b>Configurações da arena.</b> Cada valor aqui é lido pelos módulos — o horário de
        funcionamento pela agenda, a faixa de horário nobre pelo indicador de ocupação, a duração da
        sessão pelo login. {!podeEditar && 'Seu perfil só lê; peça a quem edita permissões.'}
      </Nota>

      {/*
        O `key` com o VALOR DO SERVIDOR dentro, em todo card, e ele não é
        enfeite: cada card guarda um RASCUNHO próprio, inicializado do
        valor na montagem (`useState(valor)`). Sem a chave, um valor que
        mude no servidor DEPOIS da montagem não chega aos campos — eles
        continuam mostrando o que chegou primeiro, e salvar escreve o
        valor velho por cima do novo. É o que acontece com a tela aberta
        em duas abas, ou aberta de novo enquanto o cache do react-query
        ainda serve a resposta anterior.
        `PaginaDoSite` já usa o mesmo recurso no card do formulário.
      */}
      <div className="grid g2">
        <CardHorario
          key={`horario-${chaveDoValor(valores['horario_funcionamento'])}`}
          info={item('horario_funcionamento')}
          valor={valores['horario_funcionamento'] as JanelaHoraria}
          podeEditar={podeEditar}
          chave="horario_funcionamento"
        />
        <CardFaixaNobre
          key={`nobre-${chaveDoValor(valores['faixa_horario_nobre'])}`}
          info={item('faixa_horario_nobre')}
          valor={valores['faixa_horario_nobre'] as FaixaHorarioNobre}
          podeEditar={podeEditar}
        />
        <CardPercentual
          key={`multa-${chaveDoValor(valores['multa_percentual'])}`}
          info={item('multa_percentual')}
          valor={valores['multa_percentual'] as number}
          podeEditar={podeEditar}
          chave="multa_percentual"
        />
        <CardPercentual
          key={`juros-${chaveDoValor(valores['juros_mes_percentual'])}`}
          info={item('juros_mes_percentual')}
          valor={valores['juros_mes_percentual'] as number}
          podeEditar={podeEditar}
          chave="juros_mes_percentual"
        />
        <CardDuracaoSessao
          key={`sessao-${chaveDoValor(valores['duracao_sessao_horas'])}`}
          info={item('duracao_sessao_horas')}
          valor={valores['duracao_sessao_horas'] as DuracaoSessaoHoras}
          perfis={perfis.data?.perfis ?? []}
          podeEditar={podeEditar}
        />
        <CardTexto
          key={`termo-${chaveDoValor(valores['versao_termo_vigente'])}`}
          info={item('versao_termo_vigente')}
          valor={String(valores['versao_termo_vigente'])}
          podeEditar={podeEditar}
          chave="versao_termo_vigente"
          placeholder="1.2"
        />
        <CardTexto
          key={`fuso-${chaveDoValor(valores['fuso'])}`}
          info={item('fuso')}
          valor={String(valores['fuso'])}
          podeEditar={podeEditar}
          chave="fuso"
          placeholder="America/Sao_Paulo"
        />
        {/* 12 · planos */}
        <CardDiasDaRegua
          key={`regua-${chaveDoValor(valores['regua_dias_apos_vencimento'])}`}
          info={item('regua_dias_apos_vencimento')}
          valor={(valores['regua_dias_apos_vencimento'] as number[] | undefined) ?? []}
          podeEditar={podeEditar}
        />
      </div>
    </>
  );
}

/**
 * O valor do servidor reduzido a texto, para entrar no `key` do card.
 *
 * `JSON.stringify` e não o valor cru porque metade das chaves é objeto
 * ou lista. Valor igual produz chave igual — então uma releitura que
 * trouxe o mesmo valor NÃO remonta o card, e quem está digitando não
 * perde o que digitou.
 */
function chaveDoValor(valor: unknown): string {
  return JSON.stringify(valor ?? null);
}

function Cabecalho() {
  return (
    <div className="page-head">
      <div>
        <h1>Configurações da arena</h1>
        <p>Um valor por chave — o que os outros módulos leem.</p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------
// O esqueleto comum a todo card: rótulo, descrição, rodapé com Salvar.
// ---------------------------------------------------------------------

/**
 * Gravar uma chave, com a mensagem que o servidor DE FATO devolveu.
 *
 * O detalhe que não é detalhe: numa recusa de validação o corpo do erro
 * traz `erro: "Dados inválidos."` e `campos: { fim: ["O horário de
 * abertura precisa ser anterior ao de fechamento."] }`. Mostrar só o
 * primeiro é mostrar "Dados inválidos." para quem trocou 22:00 por
 * 08:00 — a tela sabia o problema e não dizia qual. Quem digita conclui,
 * com razão, que a API está com problema.
 *
 * Então a mensagem exibida é a dos CAMPOS quando ela existe, e a
 * genérica só quando não existe.
 */
function useGravar(chave: ChaveConfiguracao) {
  const cliente = useQueryClient();
  const avisar = useToast();
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  async function salvar(valor: unknown) {
    setErro(null);
    setSalvando(true);
    try {
      await configuracoesApi.gravar(chave, valor);
      // TODA query, e não só `['configuracoes']`: o horário de
      // funcionamento desenha a grade da agenda, o fuso decide a hora em
      // toda tela, a multa e o juro entram no total de #m-pagamento. Sem
      // isto, a pessoa salva o horário novo, abre a agenda e vê a janela
      // ANTIGA — o cache dela não sabe que a configuração mudou, e o
      // sintoma é indistinguível de "não salvou".
      //
      // Invalidar tudo é grosso de propósito: configuração muda uma vez
      // por mês, e uma lista cirúrgica de chave → queries envelheceria
      // errada no primeiro módulo novo que lesse uma configuração.
      void cliente.invalidateQueries();
      avisar('Configuração salva.');
      return true;
    } catch (e) {
      setErro(detalheDoErro(e));
      return false;
    } finally {
      setSalvando(false);
    }
  }

  return { salvar, salvando, erro, limparErro: () => setErro(null) };
}

/** As mensagens por campo, se houver; senão, a do erro. */
function detalheDoErro(e: unknown): string {
  if (e instanceof ErroDeApi && e.campos) {
    const mensagens = [...new Set(Object.values(e.campos).flat())];
    if (mensagens.length > 0) return mensagens.join(' ');
  }
  return mensagemDoErro(e);
}

function Rodape({
  erro,
  podeEditar,
  sujo,
  salvando,
  onSalvar,
}: {
  erro: string | null;
  podeEditar: boolean;
  sujo: boolean;
  salvando: boolean;
  onSalvar: () => void;
}) {
  return (
    <>
      {erro && (
        <p className="sub" style={{ color: 'var(--red-lt)' }} role="alert">
          {erro}
        </p>
      )}
      {podeEditar && (
        <div style={{ marginTop: 10 }}>
          <Botao pequeno disabled={!sujo || salvando} onClick={onSalvar}>
            Salvar
          </Botao>
        </div>
      )}
    </>
  );
}

// ---------------------------------------------------------------------
// Horário de funcionamento — dois campos de hora.
// ---------------------------------------------------------------------

function CardHorario({
  info,
  valor,
  podeEditar,
  chave,
}: {
  info?: ItemDoCatalogo;
  valor: JanelaHoraria;
  podeEditar: boolean;
  chave: ChaveConfiguracao;
}) {
  const [rascunho, setRascunho] = useState(valor);
  const { salvar, salvando, erro, limparErro } = useGravar(chave);
  const sujo = rascunho.inicio !== valor.inicio || rascunho.fim !== valor.fim;

  // A MESMA regra do servidor (`zJanelaHoraria`), aqui só para dizer o
  // problema sem uma ida à rede. Quem decide continua sendo o servidor:
  // o botão desabilitado é ergonomia, não validação.
  const invalido = conferirFuncionamento(rascunho);

  /**
   * Editar apaga o erro da tentativa anterior.
   *
   * Sem isto, a pessoa corrige o campo e continua lendo a recusa de
   * antes — que agora é mentira, e que ela vai interpretar como "o
   * sistema não aceita o que eu digitei".
   *
   * O rascunho fica SEMPRE canônico, e é por isso que mudar a abertura
   * também recalcula o fechamento: "Fecha 02:00" é "26:00" com abertura às
   * 08:00, e continuaria sendo se a abertura passasse para as 09:00 — mas
   * seria "02:00" de verdade numa arena que abrisse à 01:00.
   */
  const mudar = (campos: { inicio?: string; fimNoCampo?: string }) => {
    limparErro();
    setRascunho((atual) =>
      doCampo(campos.inicio ?? atual.inicio, campos.fimNoCampo ?? paraOCampo(atual.fim)),
    );
  };

  return (
    <Card titulo={info?.rotulo ?? 'Horário de funcionamento'} dica={info?.descricao}>
      <div className="form f2">
        <div className="field">
          <label htmlFor="abre">Abre</label>
          <input
            id="abre"
            type="time"
            value={rascunho.inicio}
            disabled={!podeEditar}
            onChange={(e) => mudar({ inicio: e.target.value })}
          />
        </div>
        <div className="field">
          <label htmlFor="fecha">Fecha</label>
          <input
            id="fecha"
            type="time"
            value={paraOCampo(rascunho.fim)}
            disabled={!podeEditar}
            onChange={(e) => mudar({ fimNoCampo: e.target.value })}
          />
          {invalido === null && (
            <div className="help">
              {fechaNoDiaSeguinte(rascunho) ? (
                <b>{horarioLegivel(rascunho)}</b>
              ) : (
                horarioLegivel(rascunho)
              )}
              {` · ${duracaoDaJanela(rascunho)} por dia`}
            </div>
          )}
        </div>
      </div>
      <p className="sub">
        A grade da agenda desenha só esta janela. <b>Fechar de madrugada</b>: ponha a hora da
        madrugada em "Fecha" — 02:00 com abertura às 08:00 é às 02:00 do dia seguinte, e a grade de
        cada dia segue pela noite até lá. 00:00 é a meia-noite. Estreitar a janela não apaga
        marcação nenhuma: as que caem fora deixam de aparecer na grade até a janela voltar a
        cobri-las.
      </p>
      <Rodape
        erro={invalido ?? erro}
        podeEditar={podeEditar}
        sujo={sujo && invalido === null}
        salvando={salvando}
        onSalvar={() => void salvar(rascunho)}
      />
    </Card>
  );
}

/**
 * A tradução do `<input type="time">`, nos dois sentidos.
 *
 * O campo não sabe dizer "24:00" nem "26:00": ele mostra e devolve
 * "00:00" e "02:00". O RASCUNHO guarda a forma canônica — senão `sujo`
 * acusaria mudança a cada montagem, e Salvar nasceria habilitado sem
 * ninguém ter tocado em nada. A conversão fica nas duas pontas do campo,
 * e só nelas: `janelaCanonica` é a mesma conta do schema do servidor.
 */
const paraOCampo = (fim: string): string => fechamentoLegivel(fim);
function doCampo(inicio: string, fimNoCampo: string): JanelaHoraria {
  // Campo pela metade (o usuário apagou um dígito): vai como está, e o
  // `conferirFuncionamento` diz o que falta.
  if (!HORA_MINUTO.test(inicio) || !HORA_MINUTO.test(fimNoCampo)) {
    return { inicio, fim: fimNoCampo };
  }
  return janelaCanonica({ inicio, fim: fimNoCampo });
}

/** A faixa nobre vai até a meia-noite e não vira o dia: só "00:00" se traduz. */
const fimDaFaixaDoCampo = (fim: string): string =>
  fim === '00:00' ? MEIA_NOITE_DO_FIM_DO_DIA : fim;

/** "18 h", "17h30" — quanto tempo a arena fica aberta. */
function duracaoDaJanela(janela: JanelaHoraria): string {
  const minutos = minutosDe(janela.fim) - minutosDe(janela.inicio);
  const h = Math.floor(minutos / 60);
  const m = minutos % 60;
  return m === 0 ? `${h} h` : `${h}h${String(m).padStart(2, '0')}`;
}

/** `null` quando o funcionamento serve. As mensagens são as do schema. */
function conferirFuncionamento(janela: JanelaHoraria): string | null {
  if (!HORA_MINUTO.test(janela.inicio) || !HORA_DA_JANELA.test(janela.fim)) {
    return 'Preencha as duas horas, no formato HH:MM.';
  }
  return null;
}

/**
 * `null` quando a faixa serve. A faixa de horário nobre vai até a
 * meia-noite e não vira o dia — essa regra continua de texto.
 */
function conferirJanela(janela: { inicio: string; fim: string }): string | null {
  if (!HORA_MINUTO.test(janela.inicio) || !HORA_DE_FECHAMENTO.test(janela.fim)) {
    return 'Preencha as duas horas, no formato HH:MM.';
  }
  if (janela.inicio >= janela.fim) {
    return 'O horário de abertura precisa ser anterior ao de fechamento.';
  }
  return null;
}

const HORA_MINUTO = /^([01]\d|2[0-3]):[0-5]\d$/;
const HORA_DE_FECHAMENTO = /^(([01]\d|2[0-3]):[0-5]\d|24:00)$/;
/** O fechamento canônico do funcionamento: até "47:59", a madrugada seguinte. */
const HORA_DA_JANELA = /^([0-3]\d|4[0-7]):[0-5]\d$/;

// ---------------------------------------------------------------------
// Faixa de horário nobre — hora, hora e dias da semana.
// ---------------------------------------------------------------------

const DIAS_DA_SEMANA = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

function CardFaixaNobre({
  info,
  valor,
  podeEditar,
}: {
  info?: ItemDoCatalogo;
  valor: FaixaHorarioNobre;
  podeEditar: boolean;
}) {
  const [rascunho, setRascunho] = useState(valor);
  const { salvar, salvando, erro, limparErro } = useGravar('faixa_horario_nobre');
  const sujo = JSON.stringify(rascunho) !== JSON.stringify(valor);
  const invalido =
    rascunho.dias.length === 0 ? 'Escolha ao menos um dia.' : conferirJanela(rascunho);

  /** Editar apaga a recusa anterior — ver o card do horário acima. */
  const mudar = (parcial: Partial<FaixaHorarioNobre>) => {
    limparErro();
    setRascunho((atual) => ({ ...atual, ...parcial }));
  };

  function alternarDia(dia: number) {
    limparErro();
    setRascunho((r) => ({
      ...r,
      dias: r.dias.includes(dia) ? r.dias.filter((d) => d !== dia) : [...r.dias, dia].sort(),
    }));
  }

  return (
    <Card titulo={info?.rotulo ?? 'Faixa de horário nobre'} dica={info?.descricao}>
      <div className="form f2">
        <div className="field">
          <label>Início</label>
          <input
            type="time"
            value={rascunho.inicio}
            disabled={!podeEditar}
            onChange={(e) => mudar({ inicio: e.target.value })}
          />
        </div>
        <div className="field">
          <label>Fim</label>
          <input
            type="time"
            value={paraOCampo(rascunho.fim)}
            disabled={!podeEditar}
            onChange={(e) => mudar({ fim: fimDaFaixaDoCampo(e.target.value) })}
          />
        </div>
      </div>
      <div className="field">
        <label>Dias</label>
        <div className="choices">
          {DIAS_DA_SEMANA.map((rotulo, dia) => (
            <button
              key={dia}
              type="button"
              className="choice"
              disabled={!podeEditar}
              aria-pressed={rascunho.dias.includes(dia)}
              onClick={() => alternarDia(dia)}
            >
              {rotulo}
            </button>
          ))}
        </div>
      </div>
      <Rodape
        erro={invalido ?? erro}
        podeEditar={podeEditar}
        sujo={sujo && invalido === null}
        salvando={salvando}
        onSalvar={() => void salvar(rascunho)}
      />
    </Card>
  );
}

// ---------------------------------------------------------------------
// Percentual — multa e juros.
// ---------------------------------------------------------------------

function CardPercentual({
  info,
  valor,
  podeEditar,
  chave,
}: {
  info?: ItemDoCatalogo;
  valor: number;
  podeEditar: boolean;
  chave: ChaveConfiguracao;
}) {
  const [rascunho, setRascunho] = useState(String(valor));
  const { salvar, salvando, erro } = useGravar(chave);
  const numero = Number(rascunho.replace(',', '.'));
  const sujo = numero !== valor && !Number.isNaN(numero);

  return (
    <Card titulo={info?.rotulo ?? chave} dica={info?.descricao}>
      <div className="field">
        <label>Percentual</label>
        <input
          inputMode="decimal"
          value={rascunho}
          disabled={!podeEditar}
          onChange={(e) => setRascunho(e.target.value)}
        />
      </div>
      <Rodape
        erro={erro}
        podeEditar={podeEditar}
        sujo={sujo}
        salvando={salvando}
        onSalvar={() => void salvar(numero)}
      />
    </Card>
  );
}

// ---------------------------------------------------------------------
// Duração de sessão por perfil — um campo por perfil da arena.
// ---------------------------------------------------------------------

function CardDuracaoSessao({
  info,
  valor,
  perfis,
  podeEditar,
}: {
  info?: ItemDoCatalogo;
  valor: DuracaoSessaoHoras;
  perfis: readonly PerfilNaMatriz[];
  podeEditar: boolean;
}) {
  const [rascunho, setRascunho] = useState<Record<string, number>>(valor);
  const { salvar, salvando, erro } = useGravar('duracao_sessao_horas');
  const sujo = JSON.stringify(rascunho) !== JSON.stringify(valor);

  if (!podeEditar) {
    return (
      <Card titulo={info?.rotulo ?? 'Duração da sessão por perfil'} dica={info?.descricao}>
        <p className="sub">Só quem edita permissões vê esta lista.</p>
      </Card>
    );
  }

  return (
    <Card titulo={info?.rotulo ?? 'Duração da sessão por perfil'} dica={info?.descricao}>
      <div className="form f2">
        {perfis.map((p) => (
          <div className="field" key={p.id}>
            <label>{p.nome} (horas)</label>
            <input
              type="number"
              min={1}
              max={24 * 30}
              value={rascunho[p.chave] ?? 8}
              onChange={(e) => setRascunho({ ...rascunho, [p.chave]: Number(e.target.value) || 1 })}
            />
          </div>
        ))}
      </div>
      <Rodape
        erro={erro}
        podeEditar={podeEditar}
        sujo={sujo}
        salvando={salvando}
        onSalvar={() => void salvar(rascunho)}
      />
    </Card>
  );
}

// ---------------------------------------------------------------------
// 12 · planos — a cadência da cobrança depois do vencimento.
// ---------------------------------------------------------------------

/**
 * "1, 7, 15": em quantos dias depois do vencimento a régua publica
 * `fatura.vencida`. O servidor ordena e tira os repetidos; aqui, só o
 * texto separado por vírgula que a pessoa digita.
 */
function CardDiasDaRegua({
  info,
  valor,
  podeEditar,
}: {
  info?: ItemDoCatalogo;
  valor: readonly number[];
  podeEditar: boolean;
}) {
  const [rascunho, setRascunho] = useState(valor.join(', '));
  const { salvar, salvando, erro } = useGravar('regua_dias_apos_vencimento');
  const dias = rascunho
    .split(/[,;\s]+/)
    .filter(Boolean)
    .map(Number);
  const valido = dias.length > 0 && dias.every((d) => Number.isInteger(d));
  const sujo = valido && dias.join(',') !== valor.join(',');

  return (
    <Card titulo={info?.rotulo ?? 'Cobrança depois do vencimento'} dica={info?.descricao}>
      <div className="field">
        <label>Dias depois do vencimento</label>
        <input
          value={rascunho}
          placeholder="1, 7, 15"
          disabled={!podeEditar}
          onChange={(e) => setRascunho(e.target.value)}
        />
      </div>
      <Rodape
        erro={erro}
        podeEditar={podeEditar}
        sujo={sujo}
        salvando={salvando}
        onSalvar={() => void salvar(dias)}
      />
    </Card>
  );
}

// ---------------------------------------------------------------------
// Texto — versão do termo, fuso.
// ---------------------------------------------------------------------

function CardTexto({
  info,
  valor,
  podeEditar,
  chave,
  placeholder,
}: {
  info?: ItemDoCatalogo;
  valor: string;
  podeEditar: boolean;
  chave: ChaveConfiguracao;
  placeholder: string;
}) {
  const [rascunho, setRascunho] = useState(valor);
  const { salvar, salvando, erro } = useGravar(chave);
  const sujo = rascunho !== valor;

  return (
    <Card titulo={info?.rotulo ?? chave} dica={info?.descricao}>
      <div className="field">
        <input
          value={rascunho}
          placeholder={placeholder}
          disabled={!podeEditar}
          onChange={(e) => setRascunho(e.target.value)}
        />
      </div>
      <Rodape
        erro={erro}
        podeEditar={podeEditar}
        sujo={sujo}
        salvando={salvando}
        onSalvar={() => void salvar(rascunho)}
      />
    </Card>
  );
}
